import React, { useState, useRef, createElement } from 'react';
import { TrendingUp, TrendingDown, Sun, Moon, Download, ChevronDown } from 'lucide-react';
import { MetricCard } from './MetricCard';
import { SalesChart } from './SalesChart';
import { EditModal } from './EditModal';
import { useTheme } from '../contexts/ThemeContext';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
interface WeeklySalesData {
  kw: number;
  jahr: number;
  erstgesprächeVereinbart: number;
  erstgesprächeErschienen: number;
  qualifiziert: number;
  nichtQualifiziert: number;
  verkaufsgesprächeVereinbart: number;
  verkaufsgesprächeErschienen: number;
  abschlüsse: number;
  umsatz: number;
}
export function SalesDashboard() {
  const {
    theme,
    toggleTheme
  } = useTheme();
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  // Sample data for multiple weeks - now mutable
  const [weeklyData, setWeeklyData] = useState<WeeklySalesData[]>([{
    kw: 40,
    jahr: 2025,
    erstgesprächeVereinbart: 35,
    erstgesprächeErschienen: 28,
    qualifiziert: 20,
    nichtQualifiziert: 8,
    verkaufsgesprächeVereinbart: 20,
    verkaufsgesprächeErschienen: 17,
    abschlüsse: 10,
    umsatz: 100000
  }, {
    kw: 41,
    jahr: 2025,
    erstgesprächeVereinbart: 40,
    erstgesprächeErschienen: 32,
    qualifiziert: 22,
    nichtQualifiziert: 10,
    verkaufsgesprächeVereinbart: 22,
    verkaufsgesprächeErschienen: 19,
    abschlüsse: 12,
    umsatz: 120000
  }, {
    kw: 42,
    jahr: 2025,
    erstgesprächeVereinbart: 38,
    erstgesprächeErschienen: 30,
    qualifiziert: 21,
    nichtQualifiziert: 9,
    verkaufsgesprächeVereinbart: 21,
    verkaufsgesprächeErschienen: 18,
    abschlüsse: 11,
    umsatz: 110000
  }, {
    kw: 43,
    jahr: 2025,
    erstgesprächeVereinbart: 37,
    erstgesprächeErschienen: 30,
    qualifiziert: 22,
    nichtQualifiziert: 8,
    verkaufsgesprächeVereinbart: 22,
    verkaufsgesprächeErschienen: 18,
    abschlüsse: 12,
    umsatz: 120000
  }]);
  const [selectedView, setSelectedView] = useState<'summary' | number>('summary');
  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    field: keyof WeeklySalesData | null;
  }>({
    isOpen: false,
    title: '',
    field: null
  });
  // Calculate summary data (totals across all weeks)
  const summaryData = weeklyData.reduce((acc, week) => ({
    erstgesprächeVereinbart: acc.erstgesprächeVereinbart + week.erstgesprächeVereinbart,
    erstgesprächeErschienen: acc.erstgesprächeErschienen + week.erstgesprächeErschienen,
    qualifiziert: acc.qualifiziert + week.qualifiziert,
    nichtQualifiziert: acc.nichtQualifiziert + week.nichtQualifiziert,
    verkaufsgesprächeVereinbart: acc.verkaufsgesprächeVereinbart + week.verkaufsgesprächeVereinbart,
    verkaufsgesprächeErschienen: acc.verkaufsgesprächeErschienen + week.verkaufsgesprächeErschienen,
    abschlüsse: acc.abschlüsse + week.abschlüsse,
    umsatz: acc.umsatz + week.umsatz
  }), {
    erstgesprächeVereinbart: 0,
    erstgesprächeErschienen: 0,
    qualifiziert: 0,
    nichtQualifiziert: 0,
    verkaufsgesprächeVereinbart: 0,
    verkaufsgesprächeErschienen: 0,
    abschlüsse: 0,
    umsatz: 0
  });
  // Get current data based on selected view
  const currentData = selectedView === 'summary' ? summaryData : weeklyData.find(w => w.kw === selectedView) || summaryData;
  // Calculate percentages
  const erscheinungsquoteErstgespräch = (currentData.erstgesprächeErschienen / currentData.erstgesprächeVereinbart * 100).toFixed(1);
  const quoteQualifiziert = (currentData.qualifiziert / currentData.erstgesprächeErschienen * 100).toFixed(1);
  const quoteNichtQualifiziert = (100 - Number(quoteQualifiziert)).toFixed(1);
  const erscheinungsquoteVerkauf = (currentData.verkaufsgesprächeErschienen / currentData.verkaufsgesprächeVereinbart * 100).toFixed(1);
  const abschlussquote = (currentData.abschlüsse / currentData.verkaufsgesprächeErschienen * 100).toFixed(1);
  const umsatzProErstgespräch = (currentData.umsatz / currentData.erstgesprächeErschienen).toFixed(0);
  const umsatzProVerkaufsgespräch = (currentData.umsatz / currentData.verkaufsgesprächeErschienen).toFixed(0);
  const quoteErstgesprächZuAbschluss = (currentData.abschlüsse / currentData.erstgesprächeErschienen * 100).toFixed(1);
  const handleOpenModal = (title: string, field: keyof WeeklySalesData) => {
    setModalState({
      isOpen: true,
      title,
      field
    });
  };
  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      title: '',
      field: null
    });
  };
  const handleModalSave = (kw: number, value: number) => {
    if (!modalState.field) return;
    setWeeklyData(prevData => prevData.map(week => week.kw === kw ? {
      ...week,
      [modalState.field as keyof WeeklySalesData]: value
    } : week));
  };
  const handleUpdateValue = (field: keyof WeeklySalesData, value: number) => {
    if (selectedView === 'summary') {
      // In summary view, open modal
      const fieldTitles: { [key in keyof WeeklySalesData]?: string } = {
        erstgesprächeVereinbart: 'Erstgespräche Vereinbart',
        erstgesprächeErschienen: 'Erstgespräche Erschienen',
        qualifiziert: 'Qualifiziert',
        nichtQualifiziert: 'Nicht Qualifiziert',
        verkaufsgesprächeVereinbart: 'Verkaufsgespräche Vereinbart',
        verkaufsgesprächeErschienen: 'Verkaufsgespräche Erschienen',
        abschlüsse: 'Abschlüsse',
        umsatz: 'Umsatz'
      };
      handleOpenModal(fieldTitles[field] || field, field);
    } else {
      // In specific week view, update directly
      setWeeklyData(prevData => prevData.map(week => week.kw === selectedView ? {
        ...week,
        [field]: value
      } : week));
    }
  };
  const handleUpdateUmsatzProErstgespräch = (value: number) => {
    const newUmsatz = value * currentData.erstgesprächeErschienen;
    handleUpdateValue('umsatz', newUmsatz);
  };
  const handleUpdateUmsatzProVerkaufsgespräch = (value: number) => {
    const newUmsatz = value * currentData.verkaufsgesprächeErschienen;
    handleUpdateValue('umsatz', newUmsatz);
  };
  const handleExportPDF = async () => {
    if (!dashboardRef.current) return;
    try {
      const dataUrl = await toPng(dashboardRef.current, {
        quality: 1,
        pixelRatio: 2
      });
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const imgWidth = 210;
      const imgHeight = dashboardRef.current.offsetHeight * imgWidth / dashboardRef.current.offsetWidth;
      pdf.addImage(dataUrl, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`verkaufs-dashboard-${selectedView === 'summary' ? 'gesamt' : `KW${selectedView}`}.pdf`);
    } catch (error) {
      console.error('Export failed:', error);
    }
    setShowExportMenu(false);
  };
  const handleExportCSV = () => {
    let csvContent = '';
    let filename = '';
    if (selectedView === 'summary') {
      // Export all weeks
      csvContent = 'KW,Jahr,Erstgespräche Vereinbart,Erstgespräche Erschienen,Qualifiziert,Nicht Qualifiziert,Verkaufsgespräche Vereinbart,Verkaufsgespräche Erschienen,Abschlüsse,Umsatz\n';
      weeklyData.forEach(week => {
        csvContent += `${week.kw},${week.jahr},${week.erstgesprächeVereinbart},${week.erstgesprächeErschienen},${week.qualifiziert},${week.nichtQualifiziert},${week.verkaufsgesprächeVereinbart},${week.verkaufsgesprächeErschienen},${week.abschlüsse},${week.umsatz}\n`;
      });
      filename = 'verkaufs-dashboard-gesamt.csv';
    } else {
      // Export single week
      const week = weeklyData.find(w => w.kw === selectedView);
      if (week) {
        csvContent = 'Metrik,Wert\n';
        csvContent += `KW,${week.kw}\n`;
        csvContent += `Jahr,${week.jahr}\n`;
        csvContent += `Erstgespräche Vereinbart,${week.erstgesprächeVereinbart}\n`;
        csvContent += `Erstgespräche Erschienen,${week.erstgesprächeErschienen}\n`;
        csvContent += `Qualifiziert,${week.qualifiziert}\n`;
        csvContent += `Nicht Qualifiziert,${week.nichtQualifiziert}\n`;
        csvContent += `Verkaufsgespräche Vereinbart,${week.verkaufsgesprächeVereinbart}\n`;
        csvContent += `Verkaufsgespräche Erschienen,${week.verkaufsgesprächeErschienen}\n`;
        csvContent += `Abschlüsse,${week.abschlüsse}\n`;
        csvContent += `Umsatz,${week.umsatz}\n`;
        filename = `verkaufs-dashboard-KW${week.kw}.csv`;
      }
    }
    // Create and download CSV
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setShowExportMenu(false);
  };
  const isEditable = selectedView !== 'summary';
  return <div className={`w-full min-h-screen p-4 sm:p-6 ${theme === 'dark' ? 'bg-[#1a1a1a]' : 'bg-white'}`} data-id="element-67">
      <div className="max-w-6xl mx-auto" ref={dashboardRef} data-id="element-68">
        {/* Header */}
        <div className="mb-6 sm:mb-8" data-id="element-69">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4" data-id="element-70">
            <div data-id="element-71">
              <h1 className={`text-2xl sm:text-3xl font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-black'}`} data-id="element-72">
                Verkaufs-Dashboard
              </h1>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-73">
                Übersicht Ihrer Verkaufskennzahlen
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3" data-id="element-74">
              {/* Week Selector */}
              <select value={selectedView} onChange={e => {
              setSelectedView(e.target.value === 'summary' ? 'summary' : Number(e.target.value));
            }} className={`rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 ${theme === 'dark' ? 'bg-[#2a2a2a] text-white border border-[#3a3a3a] focus:border-[#FF7A59] focus:ring-[#FF7A59]/20' : 'bg-white text-black border border-gray-300 focus:border-[#FF7A59] focus:ring-[#FF7A59]/20'}`} data-id="element-75">
                <option value="summary" data-id="element-76">📊 Gesamt</option>
                {weeklyData.map(week => <option key={week.kw} value={week.kw} data-id="element-77">
                    KW {week.kw}
                  </option>)}
              </select>
              {/* Export Dropdown */}
              <div className="relative" data-id="element-78">
                <button onClick={() => setShowExportMenu(!showExportMenu)} className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${theme === 'dark' ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' : 'bg-white text-black hover:bg-gray-50 border border-gray-200'}`} aria-label="Export options" data-id="element-79">
                  <Download className="w-4 h-4" data-id="element-80" />
                  <ChevronDown className="w-3 h-3" data-id="element-81" />
                </button>
                {showExportMenu && <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg border z-10 ${theme === 'dark' ? 'bg-[#2a2a2a] border-[#3a3a3a]' : 'bg-white border-gray-200'}`} data-id="element-82">
                    <button onClick={handleExportPDF} className={`w-full text-left px-4 py-2 text-sm rounded-t-lg transition-colors ${theme === 'dark' ? 'text-white hover:bg-[#3a3a3a]' : 'text-black hover:bg-gray-50'}`} data-id="element-83">
                      📄 Als PDF exportieren
                    </button>
                    <button onClick={handleExportCSV} className={`w-full text-left px-4 py-2 text-sm rounded-b-lg transition-colors ${theme === 'dark' ? 'text-white hover:bg-[#3a3a3a]' : 'text-black hover:bg-gray-50'}`} data-id="element-84">
                      📊 Als CSV exportieren
                    </button>
                  </div>}
              </div>
              {/* Theme Toggle */}
              <button onClick={toggleTheme} className={`p-2 rounded-lg transition-colors ${theme === 'dark' ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' : 'bg-white text-black hover:bg-gray-50 border border-gray-200'}`} aria-label="Toggle theme" data-id="element-85">
                {theme === 'dark' ? <Sun className="w-4 h-4" data-id="element-86" /> : <Moon className="w-4 h-4" data-id="element-87" />}
              </button>
            </div>
          </div>
          {/* Summary Badge */}
          {selectedView === 'summary' && <div className={`rounded-lg p-3 ${theme === 'dark' ? 'bg-[#FF7A59]/10 border border-[#FF7A59]/20' : 'bg-[#FF7A59]/5 border border-[#FF7A59]/20'}`} data-id="element-88">
              <div className="flex items-start gap-2" data-id="element-89">
                <span className="text-lg" data-id="element-90">✏️</span>
                <div data-id="element-91">
                  <p className={`text-xs font-semibold mb-0.5 ${theme === 'dark' ? 'text-[#FF9575]' : 'text-[#FF6B4A]'}`} data-id="element-92">
                    Gesamtübersicht - Werte bearbeiten
                  </p>
                  <p className={`text-xs ${theme === 'dark' ? 'text-[#FF9575]/80' : 'text-[#FF6B4A]/80'}`} data-id="element-93">
                    Klicken Sie auf einen Wert, um ihn zu bearbeiten.
                  </p>
                </div>
              </div>
            </div>}
        </div>
        {/* Erstgespräche Section */}
        <div className="mb-6" data-id="element-94">
          <h2 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-id="element-95">
            Erstgespräche
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-96">
            <MetricCard title="Erstgespräche Vereinbart" value={currentData.erstgesprächeVereinbart} subtitle="Vereinbarte Termine" icon={<TrendingUp className="w-4 h-4" data-id="element-98" />} description="Anzahl vereinbarter Erstgespräche" percentage="+12.5" trend="up" isEditable={true} onValueChange={value => handleUpdateValue('erstgesprächeVereinbart', value)} data-id="element-97" />
            <MetricCard title="Erstgespräche Erschienen" value={currentData.erstgesprächeErschienen} subtitle={`${erscheinungsquoteErstgespräch}% Erscheinungsquote`} icon={<TrendingUp className="w-4 h-4" data-id="element-100" />} description="Tatsächlich wahrgenommene Termine" percentage={erscheinungsquoteErstgespräch} trend="up" isEditable={true} onValueChange={value => handleUpdateValue('erstgesprächeErschienen', value)} data-id="element-99" />
          </div>
        </div>
        {/* Qualifizierung Section */}
        <div className="mb-6" data-id="element-101">
          <h2 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-id="element-102">
            Qualifizierung
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-103">
            <MetricCard title="Qualifiziert" value={currentData.qualifiziert} subtitle={`${quoteQualifiziert}% Quote`} icon={<TrendingUp className="w-4 h-4" data-id="element-105" />} description="Für Verkaufsgespräch qualifiziert" percentage={quoteQualifiziert} trend="up" isEditable={true} onValueChange={value => handleUpdateValue('qualifiziert', value)} data-id="element-104" />
            <MetricCard title="Nicht Qualifiziert" value={currentData.nichtQualifiziert} subtitle={`${quoteNichtQualifiziert}% Quote`} icon={<TrendingDown className="w-4 h-4" data-id="element-107" />} description="Nicht für Verkaufsgespräch geeignet" percentage={quoteNichtQualifiziert} trend="down" isEditable={true} onValueChange={value => handleUpdateValue('nichtQualifiziert', value)} data-id="element-106" />
          </div>
        </div>
        {/* Verkaufsgespräche Section */}
        <div className="mb-6" data-id="element-108">
          <h2 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-id="element-109">
            Verkaufsgespräche
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-110">
            <MetricCard title="Verkaufsgespräche Vereinbart" value={currentData.verkaufsgesprächeVereinbart} subtitle="Mit qualifizierten Interessenten" icon={<TrendingUp className="w-4 h-4" data-id="element-112" />} description="Anzahl vereinbarter Verkaufsgespräche" percentage="+15.3" trend="up" isEditable={true} onValueChange={value => handleUpdateValue('verkaufsgesprächeVereinbart', value)} data-id="element-111" />
            <MetricCard title="Verkaufsgespräche Erschienen" value={currentData.verkaufsgesprächeErschienen} subtitle={`${erscheinungsquoteVerkauf}% Erscheinungsquote`} icon={<TrendingUp className="w-4 h-4" data-id="element-114" />} description="Wahrgenommene Verkaufsgespräche" percentage={erscheinungsquoteVerkauf} trend="up" isEditable={true} onValueChange={value => handleUpdateValue('verkaufsgesprächeErschienen', value)} data-id="element-113" />
          </div>
        </div>
        {/* Abschlüsse & Umsatz Section */}
        <div className="mb-6" data-id="element-115">
          <h2 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-id="element-116">
            Abschlüsse & Umsatz
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4" data-id="element-117">
            <MetricCard title="Abschlüsse" value={currentData.abschlüsse} subtitle={`${abschlussquote}% Abschlussquote`} icon={<TrendingUp className="w-4 h-4" data-id="element-119" />} description="Erfolgreich abgeschlossene Verträge" percentage="+18.2" trend="up" isEditable={true} onValueChange={value => handleUpdateValue('abschlüsse', value)} data-id="element-118" />
            <MetricCard title="Umsatz (Auftragsvolumen)" value={`€${currentData.umsatz.toLocaleString()}`} subtitle="Aus allen Abschlüssen" icon={<TrendingUp className="w-4 h-4" data-id="element-121" />} description="Gesamtes Auftragsvolumen" percentage="+24.5" trend="up" isEditable={true} onValueChange={value => handleUpdateValue('umsatz', value)} data-id="element-120" />
          </div>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-122">
            <MetricCard title="Umsatz pro Erstgespräch" value={`€${Number(umsatzProErstgespräch).toLocaleString()}`} subtitle="Durchschnittlicher Wert" icon={<TrendingUp className="w-4 h-4" data-id="element-124" />} description="Umsatz geteilt durch erschienene Erstgespräche" percentage="+8.3" trend="up" isEditable={true} onValueChange={handleUpdateUmsatzProErstgespräch} data-id="element-123" />
            <MetricCard title="Umsatz pro Verkaufsgespräch" value={`€${Number(umsatzProVerkaufsgespräch).toLocaleString()}`} subtitle="Durchschnittlicher Wert" icon={<TrendingUp className="w-4 h-4" data-id="element-126" />} description="Umsatz geteilt durch erschienene Verkaufsgespräche" percentage="+11.7" trend="up" isEditable={true} onValueChange={handleUpdateUmsatzProVerkaufsgespräch} data-id="element-125" />
            <MetricCard title="Conversion Rate" value={`${quoteErstgesprächZuAbschluss}%`} subtitle="Erstgespräch zu Abschluss" icon={<TrendingUp className="w-4 h-4" data-id="element-128" />} description="Abschlüsse geteilt durch erschienene Erstgespräche" percentage="+5.2" trend="up" isEditable={false} data-id="element-127" />
          </div>
        </div>
        {/* Chart Section */}
        <div className="mb-4" data-id="element-129">
          <SalesChart weeklyData={weeklyData} selectedView={selectedView} data-id="element-130" />
        </div>
      </div>
      {/* Edit Modal */}
      {modalState.field && <EditModal isOpen={modalState.isOpen} onClose={handleCloseModal} title={modalState.title} field={modalState.field} weeklyData={weeklyData} onSave={handleModalSave} data-id="element-131" />}
    </div>;
}